import React, { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Scrim } from './Scrim';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onConfirm: (password?: string) => void;
  onCancel: () => void;
  requirePassword?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
}

/**
 * Confirmation modal specifically for account deletion.
 * Makes it clear what data is removed, when, and that
 * it is permanent — no "don't show again" option.
 */
export function DeleteAccountModal({
  isOpen,
  onConfirm,
  onCancel,
  requirePassword = false,
  isLoading = false,
  errorMessage,
}: DeleteAccountModalProps) {
  const { t } = useLanguage();
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setPassword('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Scrim onClick={onCancel} zIndex={300}>
      <div
        className="rounded-2xl w-full max-w-sm mx-4 shadow-xl animate-in fade-in zoom-in duration-200 bg-white text-text-primary"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex flex-col items-center text-center gap-5">
          {/* Icon */}
          <div className="w-14 h-14 rounded-full flex items-center justify-center bg-error-red/10">
            <TriangleAlert size={26} className="text-error-red" />
          </div>

          {/* Title */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-text-primary">
              {t('deleteAccount.title')}
            </h3>

            {/* Bullet list of what happens */}
            <ul className="text-sm text-left text-text-secondary space-y-2">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-error-red font-bold">•</span>
                <span>{t('deleteAccount.bullet1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-error-red font-bold">•</span>
                <span>{t('deleteAccount.bullet2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-error-red font-bold">•</span>
                <span>{t('deleteAccount.bullet3')}</span>
              </li>
            </ul>

            <p className={cn('text-xs leading-relaxed text-gray-400 pt-1')}>
              {t('deleteAccount.footer')}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-2 w-full">
            {requirePassword && (
              <div className="text-left mb-1">
                <label className="block text-xs text-gray-500 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none focus:ring-2 focus:ring-primary-brown/30"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
            )}

            {errorMessage && (
              <div className="px-3 py-2 rounded-xl bg-error-red/5 border border-error-red/20 text-error-red text-sm text-left">
                {errorMessage}
              </div>
            )}

            <button
              onClick={() => onConfirm(password)}
              disabled={isLoading || (requirePassword && !password.trim())}
              className="w-full py-3 rounded-xl font-semibold text-sm bg-error-red text-white hover:bg-error-red/90 active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Deleting…' : t('deleteAccount.confirm')}
            </button>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="w-full py-3 rounded-xl font-semibold text-sm bg-gray-100 text-text-secondary hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {t('deleteAccount.cancel')}
            </button>
          </div>
        </div>
      </div>
    </Scrim>
  );
}
